import reactUpdate from 'immutability-helper';
import React, {UpdateSpecPath} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Batch, SampleDetails} from 'constants/SampleConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import * as DataTableConstants from 'notebook/data-tables/DataTableConstants';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {getTargetSchemaIdForBatchLinkContext} from 'notebook/data-tables/link-contexts/LinkContextTargetSchemaGetter';
import {AddSamplesModalType} from 'notebook/data-tables/link-contexts/SharedConstants';
import StandaloneDataTableAddSampleModal from 'notebook/data-tables/StandaloneDataTableAddSampleModal';
import {AddStandaloneSamplesModal} from 'notebook/sample-propagation/legacy/legacy-add-samples-modal/AddStandaloneSamplesModal';
import LegacySamplesSourceCollector from 'notebook/sample-propagation/legacy/LegacySampleSourceCollector';
import {SimpleSampleFilters} from 'notebook/sample-propagation/SampleFilter';
import {Table} from 'notebook/tables/TableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import * as SampleActions from 'samples/SampleActions';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/legacy/LegacyDropdownMenuConstants';
import ModalAnchor from 'ui-library/ModalAnchor';
import fillFromWorklist from 'worklists/util/fillFromWorklist';
import {WORKLIST_FILL_LOCATION, WorklistType} from 'worklists/WorklistConstants';

interface DataTableBatchLinkContextOwnProps {
  children: (args: {
    table: Table;
    sampleIds: Array<string>;
    addMenuItems: Array<LegacyDropdownMenuItem>;
  }) => JSX.Element;
  dataTable: DataTableConstants.DataTable;
  entryId: string;
  onUpdate: (dataTable: DataTableConstants.DataTable) => void;
  note: Note;
}

interface DataTableBatchLinkContextConnectorProps {
  batchesById: {[id: string]: Batch};
}

type DataTableBatchLinkContextProps = DataTableBatchLinkContextOwnProps &
  DataTableBatchLinkContextConnectorProps;

interface State {
  openModal: AddSamplesModalType;
}

class DataTableBatchLinkContextConnector extends DataConnector<
  DataTableBatchLinkContextProps,
  DataTableBatchLinkContextConnectorProps
> {
  enhanceProps(): DataTableBatchLinkContextConnectorProps {
    return {
      batchesById: this.getOrFetchMultipleOptionalPartial(
        ManagedModelType.BATCH,
        DataTableUtil.getBatchIds(this.props.dataTable)
      ),
    };
  }
}

/**
 * DataTableBatchLinkContext is a wrapper to DataTableEditable to provide data fetching, new row insertion,
 * and conversion to the TableEditable-compatible form, where each row is linked to a chosen batch.
 */
class DataTableBatchLinkContext extends React.Component<DataTableBatchLinkContextProps, State> {
  override state: State = {openModal: AddSamplesModalType.NONE};
  sampleFilter = [SimpleSampleFilters.BATCH];

  _handleAddExternalSampleClick = (): void => {
    this.setState({openModal: AddSamplesModalType.EXTERNAL});
  };

  _handleAddInternalSampleClick = (): void => {
    this.setState({openModal: AddSamplesModalType.INTERNAL});
  };

  _handleCloseModal = (): void => {
    this.setState({openModal: AddSamplesModalType.NONE});
  };

  _handleAddBatch = (batch: Batch, numberOfRows: number): void => {
    SampleActions.handleBatchesLoaded([batch]);
    const {config, rows} = this.props.dataTable;
    this.props.onUpdate(
      reactUpdate(this.props.dataTable, {
        rows: {
          $push: _.range(numberOfRows).map((i) =>
            DataTableUtil.generateDataTableRow(config, rows.length + i, {batchId: batch.id})
          ),
        },
      } as UpdateSpecPath)
    );
    this._handleCloseModal();
  };

  _handleAddSamples = (sampleDetails: Array<SampleDetails>): void => {
    let dataTable = this.props.dataTable;
    for (const {batchInfo} of sampleDetails) {
      assert(batchInfo != null, 'Trying to insert a sample without batchInfo');
      dataTable = DataTableUtil.addRowToStandaloneSampleTable(dataTable, {batchId: batchInfo.id});
    }
    this.props.onUpdate(dataTable);
  };

  _handleAddSamplesFromWorklist = (batchIds: Array<string>): void => {
    let dataTable = this.props.dataTable;
    for (const id of batchIds) {
      dataTable = DataTableUtil.addRowToStandaloneSampleTable(dataTable, {batchId: id});
    }
    this.props.onUpdate(dataTable);
  };

  override render(): JSX.Element {
    const rowLabels = DataTableUtil.getRowLabelsForBatchDataTable(this.props.dataTable);
    const table = DataTableUtil.convertDataTableToTable(this.props.dataTable, rowLabels);
    const sampleIds = DataTableUtil.getSampleIdsForBatchDataTable(
      this.props.dataTable,
      this.props.batchesById
    );
    const addMenuItems: Array<LegacyDropdownMenuItem> = _.compact([
      {
        name: 'Add row',
        icon: 'compose',
        handleOnClick: this._handleAddExternalSampleClick,
      },
      {
        name: 'Add samples',
        icon: 'compose',
        disabled: false,
        handleOnClick: this._handleAddInternalSampleClick,
        dataTestAttrs: {component: 'DataTableFileLinkContext', element: 'addSamplesMenuItem'},
      },
      BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS
        ? {
            name: 'Add from worklist',
            icon: ModelTypeConstants.WORKLIST,
            handleOnClick: async () => {
              const worklist = await fillFromWorklist(
                [WorklistType.BATCH],
                WORKLIST_FILL_LOCATION.STANDALONE_BATCH_RESULTS_TABLE
              );
              if (worklist) {
                this._handleAddSamplesFromWorklist(worklist.itemIds);
              }
            },
          }
        : null,
    ]);
    return (
      <>
        {this.state.openModal === AddSamplesModalType.EXTERNAL && (
          <ModalAnchor open={true}>
            <StandaloneDataTableAddSampleModal
              linkType={FIELD_TYPES.BATCH_LINK.type}
              targetSchemaId={getTargetSchemaIdForBatchLinkContext(this.props.dataTable)}
              onRequestClose={this._handleCloseModal}
              onRequestSubmit={this._handleAddBatch}
            />
          </ModalAnchor>
        )}
        {this.state.openModal === AddSamplesModalType.INTERNAL && (
          <LegacySamplesSourceCollector
            note={this.props.note}
            entryId={this.props.entryId}
            limitSamplesTo={this.sampleFilter}
          >
            {(sampleSources) => (
              <ModalAnchor open={true}>
                <AddStandaloneSamplesModal
                  onRequestClose={this._handleCloseModal}
                  onSamplesSelected={this._handleAddSamples}
                  sources={sampleSources}
                />
              </ModalAnchor>
            )}
          </LegacySamplesSourceCollector>
        )}
        {this.props.children({table, sampleIds, addMenuItems})}
      </>
    );
  }
}

export default DataTableBatchLinkContextConnector.connect(DataTableBatchLinkContext) as React.ComponentClass<
  DataTableBatchLinkContextOwnProps,
  State
>;
